import { STORE_MENU_CONFIG } from '../config/storeMenuConfig.ts';

export interface StoreInfo {
  state: string;
  storeName: string;
  displayName: string;
  medical?: {
    legacyMenuId: string;
    menuId: string;
  };
  recreational?: {
    legacyMenuId: string;
    menuId: string;
  };
}

/**
 * Get all stores with their menu configurations
 * Useful for generating admin UI and store lists
 */
export function getAllStores(): StoreInfo[] {
  const stores: StoreInfo[] = [];
  
  Object.entries(STORE_MENU_CONFIG).forEach(([storeKey, config]) => {
    const [state, storeName] = storeKey.split('/');
    
    const storeInfo: StoreInfo = {
      state,
      storeName,
      displayName: formatStoreName(storeName),
    };
    
    if (config.medical) {
      storeInfo.medical = {
        legacyMenuId: config.medical.legacyMenuId,
        menuId: config.medical.menuId,
      };
    }
    
    if (config.recreational) {
      storeInfo.recreational = {
        legacyMenuId: config.recreational.legacyMenuId,
        menuId: config.recreational.menuId,
      };
    }
    
    stores.push(storeInfo);
  });
  
  return stores;
}

/**
 * Get stores grouped by state
 * Useful for organized display in admin UI
 */
export function getStoresGroupedByState(): Record<string, StoreInfo[]> {
  const stores = getAllStores();
  const grouped: Record<string, StoreInfo[]> = {};
  
  stores.forEach(store => {
    const stateKey = formatStateName(store.state);
    if (!grouped[stateKey]) {
      grouped[stateKey] = [];
    }
    grouped[stateKey].push(store);
  });
  
  return grouped;
}

/**
 * Get store info from legacy menu ID
 * Maintains backward compatibility with existing deals system
 */
export function getStoreFromLegacyMenuId(legacyMenuId: string): {
  state: string;
  storeName: string;
  displayName: string;
  menuType: 'medical' | 'recreational';
} | null {
  for (const [storeKey, config] of Object.entries(STORE_MENU_CONFIG)) {
    const [state, storeName] = storeKey.split('/');
    
    if (config.medical?.legacyMenuId === legacyMenuId) {
      return {
        state,
        storeName,
        displayName: formatStoreName(storeName),
        menuType: 'medical',
      };
    }
    
    if (config.recreational?.legacyMenuId === legacyMenuId) {
      return {
        state,
        storeName,
        displayName: formatStoreName(storeName),
        menuType: 'recreational',
      };
    }
  }
  
  return null;
}

/**
 * Get legacy menu ID from store path
 * Converts new routing structure to legacy IDs for backend compatibility
 */
export function getLegacyMenuIdFromPath(
  state: string,
  storeName: string,
  menuType: 'medical' | 'recreational'
): string | null {
  const storeKey = `${state.toLowerCase()}/${storeName.toLowerCase()}`;
  const config = STORE_MENU_CONFIG[storeKey];
  
  if (!config) return null;
  
  if (menuType === 'medical' && config.medical) {
    return config.medical.legacyMenuId;
  }
  
  if (menuType === 'recreational' && config.recreational) {
    return config.recreational.legacyMenuId;
  }
  
  return null;
}

/**
 * Format store name for display
 */
export function formatStoreName(storeName: string): string {
  return storeName
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Format state name for display
 */
export function formatStateName(state: string): string {
  const stateNames: Record<string, string> = {
    connecticut: 'Connecticut',
    maryland: 'Maryland',
    'new-jersey': 'New Jersey',
    'rhode-island': 'Rhode Island',
    vermont: 'Vermont',
  };
  
  return stateNames[state.toLowerCase()] || state;
}

/**
 * Check if a store has deals capability
 * Currently all stores with menu IDs can have deals
 */
export function storeHasDealsCapability(
  state: string,
  storeName: string,
  menuType?: 'medical' | 'recreational'
): boolean {
  const storeKey = `${state.toLowerCase()}/${storeName.toLowerCase()}`;
  const config = STORE_MENU_CONFIG[storeKey];
  
  if (!config) return false;
  
  if (menuType) {
    return menuType === 'medical' ? !!config.medical : !!config.recreational;
  }
  
  return !!(config.medical || config.recreational);
}